<template>
    <h3>Guidance</h3>

    <div class="container-fluid px-0 mt-0">
        <template v-if="!isEligibleForProduct">
            <p>You are not eligible for the integrated product.</p>
            <p>{{ ineligibleReason }}</p>
        </template>
        <template v-else>
            <p>
                <strong>Guiide</strong> has partnered with <strong>Pathlines</strong>, a pension provider, to help simplify getting the plan payments 
                wanted, track the progress of any plan and understand the investment risks involved. It is aimed at those near or at-retirement. This 
                pension product is called <strong>{{ Constants.Product.Name }}</strong>.
            </p>
            <p>
                It is the only product available where Guiide can provide the plan payments wanted. It is also the only product that can send back latest fund value
                information to Guiide. These can then be input to update your calculations to see if your plan remains on track. Given these unique features it is
                not possible to directly compare any similar products. If more are available in future these will be added. There may be other products which are
                better suited to you.
            </p>

            <sub-section :title="`Who are PathLines, the entity providing ${Constants.Product.Name}?`" :showHelp="false">
                <div class="container">
                    <div class="row align-items-center">
                        <div class="col-md-2 d-flex justify-content-center align-items-center">
                            <img src="../../../../Content/images/Providers/pathlines.png" class="d-block mx-auto img-fluid" />
                        </div>
                        <div class="col-md-8 ps-4">
                            <p>
                                Pathlines are an FCA regulated Self Invested Personal Pensions (SIPP) provider with many years experience of administering 
                                pensions for customers in both the pre and post retirement world. They currently administer around £2bn of customers 
                                pension assets.
                            </p>
                            <p>
                                Pathlines will be your pension provider and they will do everything for you. Taking your Guiide plan and the pension 
                                information you have entered into your Guiide portal, they will consolidate your pensions, receive contributions and 
                                invest your money <strong>in the Funds you choose</strong>.
                            </p>
                            <p>
                                When the time comes Pathlines will pay your income payments in line with your Guiide plan.
                            </p>
                            <p>
                                You are able to amend your Guiide plan throughout your journey to suit your income requirements and anytime you do this 
                                Guiide will let Pathlines know so your new instructions can be processed for you. You can view your pension value with 
                                Pathlines via their online portal or <strong>within Guiide</strong> at anytime. You can learn more about Pathlines by 
                                clicking on the "About Pathlines" icon.
                            </p>
                        </div>
                        <div class="col-md-2 d-flex justify-content-center align-items-center">
                            <button class="btn btn-primary" @click="onAboutClicked('https://www.pathlines.co.uk/')">About PathLines</button>
                        </div>
                    </div>
                </div>
            </sub-section>

            <sub-section title="" :showHelp="false">
                <div class="container">
                    <div class="row align-items-center pb-1 mb-4">
                        <div class="col-md-2 d-flex justify-content-center align-items-center">
                            <a class="navbar-brand skin-navbar-brand p-0" style="background-repeat: no-repeat;" target="_blank">
                                <span class="visually-hidden">Guiide</span>
                            </a>
                        </div>
                        <div class="col-md-8 ps-4">
                            <p>
                                To be clear Guiide will not be your product provider, it will only be a platform for you to 
                                model a plan, your pension will not be with Guiide it will be with Pathlines.
                            </p>
                            <p>
                                We support tens of thousands of savers like you with our planning tools to build an initial 
                                sustainable plan and we will continue to support you with our tracking tools up to, at and
                                through into retirement.
                            </p>
                        </div>
                        <div class="col-md-2 d-flex justify-content-center align-items-center">
                            <button class="btn btn-primary" @click="onAboutClicked('https://www.corp.guiide.co.uk/index')">About us</button>
                        </div>
                    </div>
                </div>
            </sub-section>

            <pension-pots-viewer
                ref="pensionPotViewer"
                title="Pensions to Transfer"
                :show-valuation="false"
                :show-est-retirement-value="false"
                :showAddButton="false"
                :showEditButtons="false"
                :showAdvice="false"
                :showHelp="false"               
                :selectionEnabled="true"
                selectedColumnTitle="Transfer"
                :show-pathlines-alert="true"
                :show-present-total-value="true"
                v-model:selected-pension-pot-ids="selectedPensionPotIds"
                :selectable-pension-pot-ids="eligiblePensionPotIds"
                :current-value-help-text="pensionsViewerCurrentValueHelpText">
                <template v-slot:lower-area>
                    <div class="alert my-2" :class="`alert-primary`" role="alert">
                        <i class="bi bi-exclamation-circle-fill me-2"></i>
                        Please note Pathlines cannot accept any transfers where cash or income has already been accessed from
                        them. They also cannot accept any transfers where your employer is also making contributions, or your pot
                        has any valuable guaranteed benefits such as a fixed annuity rate as you will lose these employer
                        contributions or guaranteed benefits. Pathlines will check this as part of their transfer process to ensure this
                        is not the case and may inform you some pots may not be transferable to them.
                    </div>
                </template>
            </pension-pots-viewer>

            <sub-section title="Modelling investment funds" :showHelp="false">
                <p class="fw-bold">There are many type of investment funds, but very broadly they can be split into three types:</p>
                <p>
                    <strong>Money (Cash) funds</strong> - Expected returns come from short term interest like payments. Keeping some of your 
                    pension pot in these funds can be a good idea as it means your immediate income and cash lump needs can always be met. This
                    can help when markets are down as it means you don't need to sell investments when prices are low.
                </p>
                <p>
                    <strong>Income funds</strong> - Expected returns come largely from income payments. This type of fund usually keeps paying 
                    an income even if markets drop. This can also help you avoid having to sell investments during any downturn and help to 
                    protect your pension pot value over the long term.
                </p>
                <p>
                    <strong>Growth funds</strong> - Expected returns come largely from market growth. For your pension pot to last a lifetime, 
                    you'll need it to grow. Keeping some of your pension pot invested in growth investments - such as funds that invest in company
                    shares - can help you grow your pension pot. However, there is risk involved and the value of shares will go up and down. If 
                    they go down they may not recover for some time, if at all.
                </p>
            </sub-section>

            <investment-allocation
                ref="investmentAllocation"
                :retiree="retiree"
                :selected-pension-pot-ids="selectedPensionPotIds"
                @invalid="investmentAllocationsInvalid">
            </investment-allocation>

            <sub-section title="Important points to consider" :showHelp="false">
                <p>
                    Annually reviewing and tracking your plan is key. At any annual review (in March each year), you can change your future 
                    payments and investment fund choices if you wish to.
                </p>
                <p>
                    The above allows you to better understand the investment risks involved. It is not a suggestion as to how much risk is 
                    appropriate for you, or how you should invest. <strong>If you require a recommendation on these points you must seek 
                    independent financial advice.</strong>
                </p>
                <p>
                    We can pass the modelling above to Pathlines to help with the {{ Constants.Product.Name }} signup process. <strong>However any actual investment
                    choice confirmation must be made in the Pathlines application process.</strong> More information on the three current funds 
                    available is provided within the Pathlines application process.
                </p>
                <p>
                    There are currently only three available funds (one of each) to choose from in the {{ Constants.Product.Name }} product. In future, Pathlines may add alternative
                    funds and investment managers to choose from, or switch to, within {{ Constants.Product.Name }}. Other providers may offer a much wider range of funds and
                    investment choices. These may be more suitable for your circumstances. Other providers can also be used alongside {{ Constants.Product.Name }} if you choose.
                </p>
                <p>
                    Regarding investment allocations, Pathlines will first invest {{ FundAllocationCalculator.TrusteeAccountPercentage }}% of the total transfer
                    value in the {{ fundInfo.trusteeBankAccount.name }}, then the amount you confirm to Pathlines you want in the {{ fundInfo.moneyFund.name }}, followed
                    by the {{ fundInfo.incomeFund.name }}. Finally any residual amount (if any) will be invested in the {{ fundInfo.responsibleGrowthFund.name }}, until
                    everything transferred to Pathlines is allocated.
                </p>
                <p>
                    Guiide and Pathlines have no commercial relationship with any investment manager who provides funds in the {{ Constants.Product.Name }} product. To be
                    completely clear Guiide does have a commercial interest in the success of the {{ Constants.Product.Name }} product and will receive some ongoing payments from
                    Pathlines should you choose it.
                </p>
            </sub-section>

            <sub-section title="Questions you may have" :showHelp="false">
                <p>
                    We have detailed commonly asked questions below but if you would like to know anything not covered here please email
                    us at contact@guiide.co.uk.
                </p>
                <question-and-answer v-for="question, i in questionAndAnswerQuestions()" :key="i" :show-answer="question.showAnswerByDefault" :show-caret="!question.showAnswerByDefault" :show-border="false">
                    <template v-slot:question>{{ question.question }}</template>
                    <p v-html="question.answer"></p>
                </question-and-answer>
            </sub-section>

            <apply ref="apply" v-if="isEligibleForProduct && productLive" :is-applying="isApplying" :advice-message="applyErrorMessage" @apply="onApply"></apply>
        </template>

        <p>You can seek other providers on your own or get independent financial advice.</p>
        <p class="mb-5">You can do this via our Advisers page, or go to <a href="https://www.moneyhelper.org.uk" target="_blank">moneyhelper.org.uk</a> if you want more help on finding an adviser.</p>

        <button class="btn btn-secondary" @click="emit('back')">Back</button>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import SubSection from "../common/SubSection.vue";
import InvestmentAllocation from "./InvestmentAllocation.vue";
import QuestionAndAnswer from "../common/QuestionAndAnswer.vue";
import Apply from "./Apply.vue";
import PensionPotsViewer from "../assets/PensionPotsViewer.vue";
import { useStore } from "vuex";
import { Retiree } from "../../ts/retiree";
import { SecureSection } from "../../ts/secure/secureSection";
import questions from "../../ts/secure/questions";
import { app } from "../../ts/app";
import ScenarioOutcome from "../../ts/scenarioOutcome";
import Temporal from "@/shared/ts/temporal";
import Constants from "../../ts/constants";
import { ApplicationInfo } from "../../ts/secure/applicationInfo";
import { FundAllocationInstruction } from "@/guiide/ts/secure/fundAllocationInstruction";
import Result from "@/guiide/ts/result";
import FundAllocationCalculator from "@/guiide/ts/secure/fundAllocationCalculator";
import fundInfo from "@/guiide/ts/funds";

const emit = defineEmits<{(e: 'back'): void;}>();

const store = useStore();
const investmentAllocation = ref(null);
const apply = ref(null);
const retiree = computed<Retiree | null>(() => store.state.retiree);
const eligiblePensionPotIds = ref<number[]>([]);
const selectedPensionPotIds = ref<number[]>([]);
const questionAndAnswerQuestions = () => questions.filter(q => q.section == SecureSection.QuestionsAndAnswers);
const ineligibleReason = ref("");
const invalidAllocationsFound = ref(false);
const isApplying = ref(false);
const applyErrorMessage = ref("");
let invalidAllocationsReason = "";
let selectedPensionPotIdsInitialised = false;

watch(
    () => retiree,
    (newRetiree) => {
        if (selectedPensionPotIdsInitialised || !newRetiree?.value?.pensionPots) {
            return;
        }

        selectedPensionPotIds.value = newRetiree.value.pensionPots.pots.filter(pp => pp.employerMonthlyContribution === 0).map(pp => pp.id);
        eligiblePensionPotIds.value = Array.from(selectedPensionPotIds.value);
        selectedPensionPotIdsInitialised = true;
    },
    { immediate: true }
)

const productLive = computed<boolean>(() => { return store.state.configuration.secureProductApplyActive; });

async function onApply() {
    isApplying.value = true;
    applyErrorMessage.value = "";

    if (selectedPensionPotIds.value.length == 0) {
        applyErrorMessage.value = "Please select the pensions you wish to transfer.";
        isApplying.value = false;
        return;
    }

    const allocationInfo: FundAllocationInstruction = investmentAllocation.value.getAllocationInstruction();

    if (!allocationInfo.isValid) {
        applyErrorMessage.value = allocationInfo.invalidMessage;
        isApplying.value = false;
        return;
    }

    const additionalInfoValidationResult: Result = apply.value.validateAdditionalInfo();
    if (!additionalInfoValidationResult.successful) {
        applyErrorMessage.value = additionalInfoValidationResult.message;
        isApplying.value = false;
        return;
    }

    const additionalInfo = apply.value.getAdditionalInfo();
    const updatePersonalInfoResult = await app.updateAdditionalPersonalInfo(additionalInfo);

    if (!updatePersonalInfoResult.successful) {
        applyErrorMessage.value = updatePersonalInfoResult.message;
        isApplying.value = false;
        return;
    }

    const applicationInfo: ApplicationInfo = {
        pensionPotIds: selectedPensionPotIds.value,
        matchedYears: allocationInfo.matchedYears,
        matchedYearsInWindow: allocationInfo.matchedYearsInWindow,
        taxFreeLumpSumFundId: allocationInfo.taxFreeLumpSumFundId,
        fundAllocCalcAtPresent: allocationInfo.fundAllocCalcAtPresent,
        fundAllocCalcAtRetirement: allocationInfo.fundAllocCalcAtRetirement
    };

    const result = await app.beginPensionTransfer(applicationInfo);

    if (!result.successful) {
        applyErrorMessage.value = result.message;
    }

    isApplying.value = false;
}

const isEligibleForProduct = computed<boolean>(() => {
    ineligibleReason.value = "";

    if (retiree.value == null) {
        return true;
    }

    if (retiree.value.alreadyDrawingPension) {
        ineligibleReason.value = "You are already drawing your pension.";
        return false;
    }

    if (retiree.value.pensionPots.pots.length == 0) {
        ineligibleReason.value = "You do not have any pension pots.";
        return false;
    }

    if (retiree.value.pensionPots.pots.every(pp => pp.employerMonthlyContribution > 0)) {
        ineligibleReason.value = "You do not have any eligible pension pots as all of your pots have an employer contribution.";
        return false;
    }

    const currentAge = Temporal.calcAge(new Date(retiree.value.dateOfBirth));

    if (currentAge < Constants.Product.MinEligibleAge) {
        ineligibleReason.value = `You are younger than the minimum product age of ${Constants.Product.MinEligibleAge}.`;
        return false;
    }

    if (invalidAllocationsFound.value) {
        ineligibleReason.value = invalidAllocationsReason;
        return false;
    }

    const accordingToPlanScenario = store.state.accordingToPlanScenario;

    if (accordingToPlanScenario == null) {
        return true;
    }

    if (accordingToPlanScenario.outcome == ScenarioOutcome.Fail) {
        ineligibleReason.value = "You have a shortfall in your 'according to plan' scenario.";
        return false;
    }

    return true;
});

function investmentAllocationsInvalid(reason: string) {
    invalidAllocationsFound.value = true;
    invalidAllocationsReason = reason;
}

function onAboutClicked(url: string) {
    window.open(url, "_blank");
}

const pensionsViewerCurrentValueHelpText: Array<string> = [
    "These figures are estimates based on the latest known pot value you entered. These include estimated investment returns at the rate you assumed in your plan, plus any contributions you have entered since that date.",
    "These values will fluctuate daily, and the exact figures will only be known when transferred to Pathlines."
];

</script>

<style scoped>
.questionText {
    margin-top: 18px;
    margin-bottom: 8px;
}

.warningMessage {
    color: #003d99 !important;
}

.disclaimer {
    font-size: 0.75rem !important;
}

.poweredByGuiide {
    width: 80px;
    height: auto;
}
</style>
